<script>
	import { query } from "@sinao/core-js";
	import { mapGetters, mapState } from "vuex";
	import Multiselect from "vue-multiselect";
	import { formatTableToApi, formatHeadersToApi } from "../../mixins/table";
	import i18n from "@/i18n";
	import onboardingPopoverMixin from "@/mixins/onboardingPopoverMixin";
	import { htmlToMd } from "@/mixins/methods";

	export default {
		components: {
			Multiselect
		},
		mixins: [onboardingPopoverMixin],
		data() {
			return {
				openMenu: false,
				name: ""
			};
		},
		computed: {
			...mapState({
				app: state => state.app,
				appId: state => state.app.id,
				models: state => state.models.models.filter(model => model.id > 0),
				table: state => state.billing.table,
				title: state => state.home.title,
				details: state => state.home.details
			}),
			...mapGetters(["enabledHeadersValues", "getModelIndexById"]),
			selectedModel: {
				get() {
					return this.$store.state.models.selectedModel;
				},
				set(value) {
					this.$store.commit("selectedModel", value[0]);
				}
			}
		},
		watch: {
			selectedModel() {
				if (this.selectedModel?.id) {
					this.$store.dispatch("loadModel", this.selectedModel?.id);
				}
			}
		},
		methods: {
			getIndexOfModel(id) {
				return this.models.findIndex(model => id === model.id);
			},
			async saveModel() {
				try {
					const response = await query({
						operationId: "app.documents.sales.models.create",
						params: {
							appId: this.appId
						},
						data: {
							name: this.name,
							title: this.title,
							content: [...formatTableToApi(this.table)],
							columns: formatHeadersToApi(this.enabledHeadersValues),
							details: htmlToMd(this.details)
						}
					});

					// Update the list of available models
					await this.$store.dispatch("models", this.appId);

					this.selectedModel = { name: this.name, ...response };
					this.$store.commit("selectedModel", { name: this.name, ...response });
					this.name = "";

					this.$bvToast.toast(i18n.t("Votre modèle a été enregistré avec succès"), {
						title: i18n.t("Modèle enregistré"),
						variant: "success",
						solid: true
					});
				} catch (error) {
					const status = error?.response?.status ?? null;

					if (status === 429) {
						const h = this.$createElement;

						const vNodesMsg = h("div", { class: "text-center " }, [
							i18n.t("Vous avez atteint le nombre maximum de modèle que vous pouvez créer avec cette formule. Mettez à niveau votre formule"),
							h(
								"b-link",
								{
									props: {
										href: `${this.app.app.url}settings/subscription/select/`,
										target: "_blank"
									}
								},
								i18n.t(" en cliquant ici.")
							)
						]);

						this.name = "";
						this.$bvToast.toast([vNodesMsg], {
							title: i18n.t("Vous ne pouvez plus créer de modèle"),
							variant: "danger",
							solid: true
						});
					}
				}
			},
			async updateModel() {
				try {
					await query({
						operationId: "app.documents.sales.models.update",
						params: {
							appId: this.appId,
							id: this.selectedModel?.id
						},
						data: {
							...this.selectedModel,
							title: this.title,
							content: [...formatTableToApi(this.table)],
							columns: formatHeadersToApi(this.enabledHeadersValues),
							details: htmlToMd(this.details)
						}
					});

					this.$bvToast.toast(i18n.t("Votre modèle a été mis à jour avec succès"), {
						title: i18n.t("Modèle mis à jour"),
						variant: "success",
						solid: true
					});

					// Update the list of available models
					await this.$store.dispatch("models", this.appId);
				} catch (error) {
					console.log(error);
				}
			},
			async deleteModel(id) {
				try {
					await query({
						operationId: "app.documents.sales.models.delete",
						params: {
							appId: this.appId,
							id
						}
					});

					const indexOfModel = this.getModelIndexById(id);
					this.models.splice(indexOfModel, 1);
					await this.$store.dispatch("models", this.appId);

					this.$bvToast.toast(i18n.t("Votre modèle a été supprimé avec succès"), {
						title: i18n.t("Modèle supprimé"),
						variant: "success",
						solid: true
					});
				} catch (error) {
					console.log(error);
				}
			}
		}
	};
</script>

<template>
	<div>
		<Multiselect
			v-model="selectedModel"
			:placeholder="selectedModel ? '' : $t('Utiliser un modele')"
			:options="models"
			:show-labels="false"
			label="name"
			track-by="id"
			:option-height="104"
			class="floating-model-menu"
			:multiple="true"
			:max="1"
		>
			<template #noOptions>
				{{ $t("La liste est vide.") }}
			</template>

			<!-- Options on the select -->
			<template slot="option" slot-scope="props">
				<div class="option__desc">
					<span class="option__title">
						{{ props.option.name }}
					</span>
					<span>
						<b-badge
							v-if="props.option.count_usage_in_recurring_invoices > 0"
							v-b-tooltip.hover.top="
								$t('{count} utilisation(s) dans des factures récurrentes. Pour supprimer ce modèle, supprimer ses liaisons dans les factures qui l’utilisent.', { count: props.option.count_usage_in_recurring_invoices })
							"
							class="mr-2"
							variant="dark"
						>
							{{ props.option.count_usage_in_recurring_invoices }}
						</b-badge>
						<b-icon icon="trash-fill" class="option__trash" @click.stop="deleteModel(props.option.id)" />
					</span>
				</div>
			</template>

			<template slot="noOptions">
				{{ $t("La liste est vide.") }}
			</template>

			<template slot="maxElements">
				{{ $t("Retirez d’abord le modèle sélectionné pour pouvoir sélectionner ou supprimer un modèle.") }}
			</template>
		</Multiselect>

		<b-button
			v-if="!selectedModel"
			v-b-modal.FloatingModel
			variant="light"
			size="sm"
			block
			class="mt-2 d-flex align-items-center"
		>
			<b-icon icon="file-post" />
			<span class="mx-auto"> {{ $t("Enregistrer comme modèle") }} </span>
		</b-button>

		<b-button v-else variant="light" size="sm" block class="mt-2 d-flex align-items-center" @click="updateModel()">
			<b-icon icon="file-post" />
			<span
				v-b-tooltip.hover.top="
					selectedModel && selectedModel.count_usage_in_recurring_invoices > 0
						? $t('Attention {count} facture(s) récurrente(s) seront impactée(s).', { count: selectedModel.count_usage_in_recurring_invoices })
						: ''
				"
				class="mx-auto"
			>
				{{ $t("Mettre à jour le modèle") }}
			</span>
		</b-button>

		<b-modal
			id="FloatingModel"
			:title="$t('Enregistrer un modèle')"
			:ok-title="$t('Valider')"
			:cancel-title="$t('Annuler')"
			@ok="saveModel"
			@show="onModalShow"
			@hidden="onModalHidden"
		>
			<b-form-input
				v-model.lazy="name"
				:placeholder="$t('Nom de mon modèle')"
			/>
		</b-modal>
	</div>
</template>

<style lang="scss">
	.floating-model-menu {
		.option {
			&__title {
				align-items: center;
				display: flex;
			}

			&__desc {
				width: 100%;
				display: flex;
				justify-content: space-between;
			}

			&__trash.b-icon {
				font-size: 20px;
			}
		}

		.multiselect__option {
			padding: 10 px;
		}
	}
</style>
